import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import chatService, {
  CHAT_MESSAGES_PAGE_SIZE,
  type Thread,
  type Message,
  chatEvents,
} from '../../services/chatService';
import { tagsApi } from '../tags/tagsApi';

// Local types
type ConnectionStatus = any;

type TypingByThread = Record<string, boolean>;

export interface ChatState {
  // State
  isOpen: boolean;
  selectedThreadId: string | null;
  threads: Thread[];
  messages: Message[];
  unreadCount: number;
  isConnected: boolean;
  connectionStatus: ConnectionStatus | null;

  // Loading
  threadsLoading: boolean;
  threadsLoadFailed: boolean;
  messagesLoading: boolean;
  sendingMessage: boolean;
  switchingThread: boolean;

  // Settings
  soundEnabled: boolean;
  isDocked: boolean;

  // Typing
  typingByThread: TypingByThread;

  // UI helper
  firstNewMessageId: string | null;

  // Internal throttles and guards
  lastThreadsRefreshAt: number;
  lastMessagesRefreshAt: number;
  
  // Pagination
  nextCursor: string | null;
  loadingOlder: boolean;
  olderMessagesRequestId: string | null;
  threadsNextCursor: string | null;
  threadsHasMore: boolean;
  threadsLoadingMore: boolean;
  prewarmed: boolean;
  openedAt: number;
  // Guard to avoid SSE refresh loops after local markAsRead
  markReadGuard: Record<string, number>;
}

// Utilities
function now() {
  return Date.now();
}

function latestReplyWindowDeadline(
  left?: string | null,
  right?: string | null,
): string | null {
  const leftMs = Date.parse(left || '');
  const rightMs = Date.parse(right || '');
  if (!Number.isFinite(leftMs)) return Number.isFinite(rightMs) ? right! : null;
  if (!Number.isFinite(rightMs)) return left!;
  return rightMs > leftMs ? right! : left!;
}

// Reconcile message arrays to preserve object identity and avoid re-render flicker
function reconcileMessages(prev: Message[], next: Message[]): Message[] {
  if (!Array.isArray(next)) return prev;

  const prevById = new Map(prev.map((m) => [m.id, m]));
  const out: Message[] = [];

  // 1) Base reconciliation to preserve identity of unchanged messages coming from "next"
  for (const m of next) {
    const old = prevById.get(m.id);
    if (
      old &&
      old.fromMe === m.fromMe &&
      old.text === m.text &&
      old.imageUrl === m.imageUrl &&
      JSON.stringify(old.attachments) === JSON.stringify(m.attachments) &&
      old.createdAt === m.createdAt &&
      old.status === m.status &&
      old.senderKind === m.senderKind &&
      old.origin === m.origin &&
      (old as any).botName === (m as any).botName
    ) {
      out.push(old);
    } else if (old) {
      out.push({ ...old, ...m });
    } else {
      out.push(m);
    }
  }

  // 2) Retain optimistic SSE bot/auto messages until canonical arrives
  //    and drop them automatically once the API returns the canonical.
  try {
    const FIVE_MIN = 5 * 60 * 1000;
    const norm = (s?: string) => String(s || '').trim();
    const existsById = new Set(out.map((m) => m.id));

    const nextFromMe = (Array.isArray(next) ? next : []).filter((m) => m?.fromMe);

    const isBotish = (m: Message) => {
      const k = String(m?.senderKind || '').toLowerCase();
      const o = String((m as any)?.origin || '').toLowerCase();
      return k === 'bot' || k === 'auto' || o === 'n8n';
    };

    for (const opt of prev) {
      const tempId = String((opt as any)?.tempId || '');
      const sseBotOptimistic =
        tempId.startsWith('sse_bot_') && opt.fromMe === true && isBotish(opt);

      if (!sseBotOptimistic) continue;

      const tOpt = Date.parse(opt.createdAt || '');
      const matchedCanonical = nextFromMe.some((c) => {
        const tCan = Date.parse(c.createdAt || '');
        return (
          c.fromMe === true &&
          norm(c.text) === norm(opt.text) &&
          Math.abs((tCan || 0) - (tOpt || 0)) <= FIVE_MIN
        );
      });

      // If canonical not present yet, keep the optimistic so UI doesn't flicker it away
      if (!matchedCanonical && !existsById.has(opt.id)) {
        out.push(opt);
        existsById.add(opt.id);
      }
      // If matched, we do nothing: out already contains canonical and optimistic is naturally dropped.
    }
  } catch {
    // best-effort; never throw from reconciliation
  }

  return out;
}

function mergeMessagePages(prev: Message[], next: Message[]): Message[] {
  const byId = new Map<string, Message>();
  for (const message of [...prev, ...next]) {
    const old = byId.get(message.id);
    byId.set(message.id, old ? { ...old, ...message } : message);
  }
  const combined = Array.from(byId.values()).sort((a, b) => {
    const left = Date.parse(a.createdAt || '');
    const right = Date.parse(b.createdAt || '');
    if (!Number.isFinite(left) || !Number.isFinite(right)) return 0;
    return left - right;
  });
  return reconcileMessages(prev, combined);
}

const initialState: ChatState = {
  isOpen: false,
  selectedThreadId: null,
  threads: [],
  messages: [],
  unreadCount: 0,
  isConnected: false,
  connectionStatus: null,

  threadsLoading: false,
  threadsLoadFailed: false,
  messagesLoading: false,
  sendingMessage: false,
  switchingThread: false,

  soundEnabled: chatService.isSoundEnabled(),
  isDocked: (() => {
    try {
      return localStorage.getItem('chat.isDocked') === 'true';
    } catch {
      return false;
    }
  })(),
  typingByThread: {},

  firstNewMessageId: null,

  lastThreadsRefreshAt: 0,
  lastMessagesRefreshAt: 0,
  prewarmed: false,
  openedAt: Date.now(),
  markReadGuard: {},
  nextCursor: null,
  loadingOlder: false,
  olderMessagesRequestId: null,
  threadsNextCursor: null,
  threadsHasMore: false,
  threadsLoadingMore: false,
};

// Thunks

export const openChat = createAsyncThunk('chat/openChat', async () => {
  // No side effect needed, slice reducer flips flag
  return true;
});

export const closeChat = createAsyncThunk('chat/closeChat', async () => {
  // No side effect needed
  return true;
});

export const togglePin = createAsyncThunk(
  'chat/togglePin',
  async (args: { threadId: string; isPinned: boolean }, _api) => {
    await chatService.togglePin(args.threadId, args.isPinned);
    return true;
  }
);

export const markAsRead = createAsyncThunk('chat/markAsRead', async (threadId: string) => {
  await chatService.markAsRead(threadId);
  return { threadId };
});

export const refreshThreads = createAsyncThunk(
  'chat/refreshThreads',
  async (_, { getState }) => {
    const state = getState() as any;
    const { chat } = state;
    const nowTs = now();
    if (nowTs - chat.lastThreadsRefreshAt < 1500) {
      // Throttled: return current cached threads
      const cached = await chatService.getThreads(false);
      return { threads: Array.isArray(cached) ? cached : [], nextCursor: chat.threadsNextCursor, hasMore: chat.threadsHasMore, at: nowTs, throttled: true };
    }
    const page = await chatService.getThreadsPage({ forceRefresh: true, limit: 50 });
    return { threads: page.threads, nextCursor: page.nextCursor, hasMore: page.hasMore, at: nowTs, throttled: false };
  }
);

export const loadMoreThreads = createAsyncThunk(
  'chat/loadMoreThreads',
  async (_, { getState }) => {
    const chat = (getState() as any).chat as ChatState;
    if (chat.threadsLoadingMore || !chat.threadsHasMore || !chat.threadsNextCursor) {
      return { skipped: true };
    }
    const page = await chatService.getThreadsPage({ cursor: chat.threadsNextCursor, limit: 50, forceRefresh: true });
    return { ...page, skipped: false };
  }
);

export const refreshMessages = createAsyncThunk(
  'chat/refreshMessages',
  async (args: { forceNetwork?: boolean } | undefined, { getState }) => {
    const state = getState() as any;
    const { chat } = state as { chat: ChatState };
    const selectedThreadId = chat.selectedThreadId;
    if (!selectedThreadId) {
      return { threadId: null as string | null, messages: [] as Message[], switchingDone: true, boundary: null };
    }
 
    // Snapshot unread counts BEFORE any clearing so we can compute the boundary
    const row: any = Array.isArray(chat.threads)
      ? chat.threads.find((t: any) => String(t.id) === String(selectedThreadId))
      : null;
    const preUnreadClient = Number(row?.unreadClientCount || 0);
    const preUnreadBot = Number(row?.unreadBotCount || 0);
    const preUnreadTotal = Math.max(Number(row?.unreadCount || 0), preUnreadClient + preUnreadBot);
 
    const computeBoundary = (list: Message[]): string | null => {
      try {
        let neededClient = preUnreadClient;
        let neededBot = preUnreadBot;
        if (!Array.isArray(list) || list.length === 0 || (neededClient <= 0 && neededBot <= 0)) return null;
        let boundaryId: string | null = null;
        for (let i = list.length - 1; i >= 0; i--) {
          const m = list[i];
          if (!m.fromMe) {
            if (neededClient > 0) { neededClient -= 1; boundaryId = m.id; }
          } else {
            // Assume any fromMe message that isn't explicitly human is a bot message
            const kind = String(m.senderKind || '').toLowerCase();
            const origin = String(m.origin || '').toLowerCase();
            const isBot = kind === 'bot' || kind === 'auto' || origin === 'n8n' || !!m.botName;
            if (isBot && neededBot > 0) {
              neededBot -= 1;
              boundaryId = m.id;
            } else if (!isBot) {
              // If we see a human message from me, it means we've read up to here in our own app.
              // We could break here, but let's just continue evaluating.
            }
          }
          if (neededClient <= 0 && neededBot <= 0) break;
        }
        return boundaryId;
      } catch {
        return null;
      }
    };
 
    const forceNetwork = !!(args && args.forceNetwork);
    const nowTs = now();
 
    if (!forceNetwork) {
      const willFetch = (chatService as any).willFetchMessages?.(selectedThreadId) ?? true;
      // If we won't hit the network, return cached messages and compute separator, then mark read
      if (!willFetch) {
        await chatService.getMessages(selectedThreadId, false);
        const cachedPage = chatService.getMessagesCachedPage(selectedThreadId);
        const messages = Array.isArray(cachedPage.messages) ? cachedPage.messages : [];
        const boundary = computeBoundary(messages);
        if (preUnreadTotal > 0) {
          // Unread state is no longer cleared here; it is handled by the UI scroll to bottom
        }
        return {
          threadId: selectedThreadId,
          messages,
          switchingDone: true,
          boundary,
          at: nowTs,
          nextCursor: cachedPage.nextCursor,
        };
      }
 
      // Throttle message refreshes per thread (min 1000ms), but not the first load after a thread switch
      const throttled = !forceNetwork && (nowTs - chat.lastMessagesRefreshAt < 1000);
      if (throttled && !chat.switchingThread) {
        await chatService.getMessages(selectedThreadId, false).catch(() => []);
        const cachedPage = chatService.getMessagesCachedPage(selectedThreadId);
        const messages = Array.isArray(cachedPage.messages) ? cachedPage.messages : [];
        const boundary = computeBoundary(messages);
        if (preUnreadTotal > 0) {
          // Unread state is no longer cleared here; it is handled by the UI scroll to bottom
        }
        return {
          threadId: selectedThreadId,
          messages,
          switchingDone: true,
          boundary,
          at: nowTs,
          nextCursor: cachedPage.nextCursor,
        };
      }
    }
 
    // Fresh fetch (forced or not throttled)
    const page = await chatService.getMessagesPage(selectedThreadId);
    const updated = page.messages;
    const messages = Array.isArray(updated) ? updated : [];
    const boundary = computeBoundary(messages);
    if (preUnreadTotal > 0) {
      // Unread state is no longer cleared here; it is handled by the UI scroll to bottom
    }
    return {
      threadId: selectedThreadId,
      messages,
      switchingDone: true,
      boundary,
      at: nowTs,
      nextCursor: page.nextCursor,
    };
  }
);
 
export const loadOlderMessages = createAsyncThunk(
  'chat/loadOlderMessages',
  async (_, { getState }) => {
    const state = getState() as any;
    const chat = state.chat as ChatState;
    const { selectedThreadId, nextCursor } = chat;
    if (!selectedThreadId || !nextCursor) {
      throw new Error("No cursor or thread");
    }
    const page = await chatService.getMessagesPage(selectedThreadId, nextCursor);
    return {
      threadId: selectedThreadId,
      messages: Array.isArray(page.messages) ? page.messages : [],
      nextCursor: page.nextCursor,
    };
  },
  {
    condition: (_, { getState }) => {
      const chat = (getState() as any).chat as ChatState;
      return !!chat.selectedThreadId && !!chat.nextCursor && !chat.loadingOlder;
    },
  }
);

export const selectThread = createAsyncThunk(
  'chat/selectThread',
  async (threadId: string | null, { getState, dispatch }) => {
    const state = getState() as any;
    const { chat } = state as { chat: ChatState };

    // Allow switching to a different thread even if a previous switch is in progress.
    // Only block if the user clicks again on the same currently-selected thread while it's still switching.
    if (chat.switchingThread && threadId === chat.selectedThreadId) {
      return { selectedThreadId: chat.selectedThreadId };
    }

    if (threadId && threadId === chat.selectedThreadId) {
      // Ensure ChatService knows the current thread for SSE optimistic decisions
      try { (chatService as any).setCurrentThreadId?.(threadId); } catch {}
      if ((chatService as any).isThreadDirty?.(threadId)) {
        // Ensure no throttle blocks this refresh
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        dispatch(setLastMessagesRefreshAt(0));
        // refresh now
        await dispatch(refreshMessages());
      }
      return { selectedThreadId: chat.selectedThreadId };
    }

    // Reset new-messages marker when switching threads
    dispatch(setOpenedAt(Date.now()));
    dispatch(setFirstNewMessageId(null));

    if (threadId) {
      const willFetch = (chatService as any).willFetchMessages?.(threadId) ?? true;

      // Inform ChatService about current thread for SSE optimistic insertion
      try { (chatService as any).setCurrentThreadId?.(threadId); } catch {}

      // Update selection first
      dispatch(setSelectedThreadId(threadId));

      // Instant render from in-memory cache (no await/no blank), then selective background refresh
      let hasCache = false;
      try {
        const cachedPage = chatService.getMessagesCachedPage(threadId);
        const cached = cachedPage.messages;
        hasCache = Array.isArray(cached) && cached.length > 0;
        dispatch(setMessages(reconcileMessages((state as any).chat.messages, Array.isArray(cached) ? cached : [])));
        dispatch(setMessagesNextCursor(cachedPage.nextCursor));
      } catch {
        // ignore cache read failures
      }

      // If the thread shows unread in the list or is known dirty, force a fetch on selection
      const threadsState: any[] = Array.isArray((state as any).chat?.threads) ? (state as any).chat.threads : [];
      const row = threadsState.find((t: any) => String(t.id) === String(threadId));
      const unreadCount = Number(row?.unreadCount || 0);
      const unreadClient = Number(row?.unreadClientCount || 0);
      const unreadBot = Number(row?.unreadBotCount || 0);
      const legacyUnread = !!row?.unread;
      const hasUnread = legacyUnread || unreadCount > 0 || unreadClient > 0 || unreadBot > 0;
      const isDirty = !!((chatService as any).isThreadDirty?.(threadId) ?? false);
      const shouldFetch = !!willFetch || hasUnread || isDirty;

      if (!hasCache) {
        dispatch(setLastMessagesRefreshAt(0));
        dispatch(setSwitchingThread(true));
        await (dispatch as any)(refreshMessages({ forceNetwork: true }));
      } else if (shouldFetch) {
        dispatch(setLastMessagesRefreshAt(0));
        dispatch(setSwitchingThread(true));
        (dispatch as any)(refreshMessages({ forceNetwork: true }));
      }

      // Mark-as-read is now performed inside refreshMessages after computing the new-messages separator.
      // We intentionally do not clear unread here to allow accurate separator calculation.
    } else {
      // No thread selected
      try { (chatService as any).setCurrentThreadId?.(null); } catch {}
      dispatch(setSelectedThreadId(null));
      dispatch(setMessages([]));
      dispatch(setMessagesNextCursor(null));
      dispatch(setFirstNewMessageId(null));
    }

    return { selectedThreadId: threadId };
  }
);

export const sendMessage = createAsyncThunk(
  'chat/sendMessage',
  async (
    args: { text?: string; imageUrl?: string; mediaUrl?: string; mediaType?: string; options?: { senderKind?: string; origin?: string; botName?: string } },
    { getState }
  ) => {
    const state = getState() as any;
    const { chat } = state as { chat: ChatState };
    const threadId = chat.selectedThreadId;
    if (!threadId || (!args.text && !args.imageUrl && !args.mediaUrl)) {
      return false;
    }

    await chatService.sendMessage(threadId, { text: args.text, imageUrl: args.imageUrl, mediaUrl: args.mediaUrl, mediaType: args.mediaType }, args.options || {});
    // Do not force-refresh threads; SSE + optimistic cache will update the list immediately.
    return true;
  }
);

export const prewarmTopMessages = createAsyncThunk('chat/prewarmTopMessages', async (_, { getState }) => {
  const state = getState() as any;
  const { chat } = state as { chat: ChatState };
  if (chat.threadsLoading) return false;
  if (!Array.isArray(chat.threads) || chat.threads.length === 0) return false;
 
  try {
    // Warm more threads (first 30 visible) to ensure instant switching even after updates
    const top = chat.threads.slice(0, 30);
    await Promise.all(top.map((t) => chatService.getMessages(t.id, false).catch(() => null)));
  } catch {
    // ignore background prewarm failures
  }
  return true;
});

export const initializeChatRuntime = createAsyncThunk('chat/initialize', async (_, { dispatch }) => {
  // Snapshot loading is independent of connection metadata and SSE availability.
  const runtime = chatService.initialize();
  void chatService.getConnectionStatus().then(status => {
    if (status) dispatch(setConnectionStatus(status));
  }).catch(() => {});
  const snapshot = await dispatch(refreshThreads());
  if (refreshThreads.fulfilled.match(snapshot)) {
    dispatch(setUnreadCount(snapshot.payload.threads.reduce((sum, thread) => sum + Number(thread.unreadCount || 0), 0)));
  }
  void dispatch(loadThreadTagsForThreads(undefined));
  void dispatch(prewarmTopMessages());
  await runtime;
  return true;
});

// Slice
export const loadThreadTagsForThreads = createAsyncThunk(
  'chat/loadThreadTagsForThreads',
  async (ids: string[] | undefined, { getState }) => {
    const state = getState() as any;
    const fromState = Array.isArray(ids) && ids.length > 0
      ? ids
      : (Array.isArray(state.chat?.threads) ? state.chat.threads.map((t: any) => String(t.id)) : []);
    const threadIds = (fromState || []).filter(Boolean).slice(0, 200);
    if (threadIds.length === 0) return {};

    // We do NOT overwrite localStorage with state.auth.user here,
    // because during auth:login events, Redux state is stale and will overwrite the new token.

    try {
      // Use ChatService to ensure identical auth/params behavior as the rest of chat runtime
      const ckFromStatus = state?.chat?.connectionStatus?.comercio_key || null;
      let mapping = await chatService.getThreadTagsBulk(threadIds, ckFromStatus || undefined);
      if (!mapping || typeof mapping !== 'object') mapping = {};
      // Fallback: if bulk returns empty (some backends), fetch per-thread for first 50 threads
      if (Object.keys(mapping).length === 0) {
        const limited = threadIds.slice(0, 50);
        const pairs = await Promise.all(
          limited.map(async (id: string) => {
            try {
              const tags = await chatService.getThreadTags(id, true);
              return [String(id), Array.isArray(tags) ? tags : []] as const;
            } catch {
              return [String(id), []] as const;
            }
          })
        );
        const agg: Record<string, any[]> = {};
        for (const [id, tags] of pairs) agg[id] = tags;
        return agg;
      }
      return mapping;
    } catch {
      return {};
    }
  }
);

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    setIsOpen(state, action: PayloadAction<boolean>) {
      state.isOpen = action.payload;
    },
    setSelectedThreadId(state, action: PayloadAction<string | null>) {
      if (state.selectedThreadId !== action.payload) {
        state.nextCursor = null;
        state.loadingOlder = false;
        state.olderMessagesRequestId = null;
        state.messagesLoading = false;
      }
      state.selectedThreadId = action.payload;
    },
    setThreads(state, action: PayloadAction<Thread[]>) {
      state.threads = action.payload || [];
    },
    // Search results can include conversations outside the currently loaded pages.
    // Keep them selectable without resetting the normal pagination cursor.
    mergeSearchThreads(state, action: PayloadAction<Thread[]>) {
      const byId = new Map((state.threads || []).map((thread) => [String(thread.id), thread]));
      for (const thread of action.payload || []) {
        const previous = byId.get(String(thread.id));
        // Search-index documents intentionally contain only summary data. Existing
        // rows may have newer SSE unread counters, tag hydration, typing and bot
        // state, so never let the search defaults (for example unreadCount: 0)
        // overwrite that operational state.
        byId.set(
          String(thread.id),
          previous
            ? {
                ...thread,
                ...previous,
                tags: previous.tags || thread.tags || [],
                replyWindowEndsAt: latestReplyWindowDeadline(
                  previous.replyWindowEndsAt,
                  thread.replyWindowEndsAt,
                ),
              }
            : thread
        );
      }
      state.threads = Array.from(byId.values());
    },
    setMessages(state, action: PayloadAction<Message[]>) {
      state.messages = reconcileMessages(state.messages, action.payload || []);
    },
    setMessagesNextCursor(state, action: PayloadAction<string | null>) {
      state.nextCursor = action.payload || null;
    },
    setUnreadCount(state, action: PayloadAction<number>) {
      state.unreadCount = Math.max(0, Number(action.payload || 0));
    },
    setIsConnected(state, action: PayloadAction<boolean>) {
      state.isConnected = !!action.payload;
    },
    setConnectionStatus(state, action: PayloadAction<ConnectionStatus | null>) {
      state.connectionStatus = action.payload || null;
    },
    setThreadsLoading(state, action: PayloadAction<boolean>) {
      state.threadsLoading = !!action.payload;
    },
    setMessagesLoading(state, action: PayloadAction<boolean>) {
      state.messagesLoading = !!action.payload;
    },
    setSendingMessage(state, action: PayloadAction<boolean>) {
      state.sendingMessage = !!action.payload;
    },
    setSwitchingThread(state, action: PayloadAction<boolean>) {
      state.switchingThread = !!action.payload;
    },
    setSoundEnabled(state, action: PayloadAction<boolean>) {
      state.soundEnabled = !!action.payload;
      try {
        chatService.setSoundEnabled(state.soundEnabled);
      } catch {}
    },
    setDocked(state, action: PayloadAction<boolean>) {
      state.isDocked = !!action.payload;
      try {
        localStorage.setItem('chat.isDocked', JSON.stringify(state.isDocked));
      } catch {}
    },
    setTyping(state, action: PayloadAction<{ threadId: string; typing: boolean }>) {
      const { threadId, typing } = action.payload;
      const key = String(threadId || '');
      if (!key) return;
      state.typingByThread = { ...state.typingByThread, [key]: typing };
      // Also mirror typing flag onto threads list snapshot for UI convenience
      state.threads = (state.threads || []).map((t) => (t.id === key ? { ...t, typing } : t));
    },
    setFirstNewMessageId(state, action: PayloadAction<string | null>) {
      state.firstNewMessageId = action.payload;
    },
    setOpenedAt(state, action: PayloadAction<number>) {
      state.openedAt = Number(action.payload || Date.now());
    },
    setPrewarmed(state, action: PayloadAction<boolean>) {
      state.prewarmed = !!action.payload;
    },
    setLastThreadsRefreshAt(state, action: PayloadAction<number>) {
      state.lastThreadsRefreshAt = Number(action.payload || 0);
    },
    setLastMessagesRefreshAt(state, action: PayloadAction<number>) {
      state.lastMessagesRefreshAt = Number(action.payload || 0);
    },
    // Local helper to clear unread for a thread
    clearThreadUnread(state, action: PayloadAction<string>) {
      const tid = String(action.payload || '');
      if (!tid) return;
      state.threads = (state.threads || []).map((t: any) =>
        t.id === tid ? { ...t, unread: false, unreadCount: 0, unreadClientCount: 0, unreadBotCount: 0 } : t
      );
    },
    // UI-compat: convenient action wrappers for open/close
    openChatUI(state) {
      state.isOpen = true;
    },
    closeChatUI(state) {
      state.isOpen = false;
    },
    // Merge tags into existing threads (used by bulk loads and SSE)
    mergeThreadTags(state, action: PayloadAction<Record<string, any[]>>) {
      try {
        const map = action.payload || {};
        state.threads = (state.threads || []).map((t: any) => {
          const tags = map[String(t.id)];
          if (Array.isArray(tags)) {
            return { ...t, tags };
          }
          return t;
        });
      } catch {}
    },
    // Guard timestamp to avoid SSE refresh loops immediately after local markAsRead
    setMarkReadGuard(state, action: PayloadAction<{ threadId: string; at: number }>) {
      const tid = String(action.payload?.threadId || '');
      if (!tid) return;
      const at = Number(action.payload?.at || Date.now());
      state.markReadGuard = { ...state.markReadGuard, [tid]: at };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadThreadTagsForThreads.fulfilled, (state, action) => {
        try {
          const map = (action.payload as any) || {};
          state.threads = (state.threads || []).map((t: any) => {
            const tags = map[String(t.id)];
            if (Array.isArray(tags)) return { ...t, tags };
            // Ensure field exists to satisfy "always visible"
            return { ...t, tags: Array.isArray((t as any).tags) ? (t as any).tags : [] };
          });
        } catch {}
      })
      .addCase(refreshThreads.pending, (state) => {
        state.threadsLoadFailed = false;
        state.threadsLoading = true;
      })
      .addCase(refreshThreads.fulfilled, (state, action) => {
        const { threads, nextCursor, hasMore, at } = action.payload as any;
        if (Number.isFinite(at)) state.lastThreadsRefreshAt = at;
        if (Array.isArray(threads)) {
          const prevById = new Map((state.threads || []).map((t: any) => [String(t.id), t]));
          state.threads = threads.map((t: any) => {
            const prev = prevById.get(String(t.id));
            const prevTags = Array.isArray(prev?.tags) ? prev.tags : [];
            // Preserve existing tags to avoid flicker; ensure field exists
            return prevTags.length
              ? { ...t, tags: prevTags }
              : { ...t, tags: Array.isArray((t as any).tags) ? (t as any).tags : [] };
          });
        }
        state.threadsNextCursor = nextCursor ? String(nextCursor) : null;
        state.threadsHasMore = !!hasMore;
        state.threadsLoading = false;
      })
      .addCase(refreshThreads.rejected, (state) => {
        state.threadsLoadFailed = true;
        state.threadsLoading = false;
      });

    builder
      .addCase(loadMoreThreads.pending, (state) => {
        state.threadsLoadingMore = true;
      })
      .addCase(loadMoreThreads.fulfilled, (state, action) => {
        state.threadsLoadingMore = false;
        const payload = action.payload as any;
        if (payload?.skipped) return;
        if (Array.isArray(payload?.threads)) {
          const previousById = new Map((state.threads || []).map((thread) => [String(thread.id), thread]));
          state.threads = payload.threads.map((thread: any) => {
            const previous = previousById.get(String(thread.id));
            const tags = Array.isArray(previous?.tags) ? previous.tags : (Array.isArray(thread.tags) ? thread.tags : []);
            return { ...thread, tags };
          });
        }
        state.threadsNextCursor = payload?.nextCursor ? String(payload.nextCursor) : null;
        state.threadsHasMore = !!payload?.hasMore;
      })
      .addCase(loadMoreThreads.rejected, (state) => {
        state.threadsLoadingMore = false;
      });

    builder
      .addCase(refreshMessages.pending, (state) => {
        state.messagesLoading = true;
      })
      .addCase(refreshMessages.fulfilled, (state, action) => {
        const { threadId, messages, switchingDone, at, boundary, nextCursor } = action.payload as any;
        if (threadId && threadId !== state.selectedThreadId) return;
        if (Number.isFinite(at)) state.lastMessagesRefreshAt = at;
        if (threadId && Array.isArray(messages)) {
          const incomingIds = new Set(messages.map((message: Message) => message.id));
          const overlaps = state.messages.some((message) => incomingIds.has(message.id));
          const retainLoadedHistory =
            state.messages.length > CHAT_MESSAGES_PAGE_SIZE && overlaps;
          state.messages = retainLoadedHistory
            ? mergeMessagePages(state.messages, messages)
            : reconcileMessages(state.messages, messages);
          if (!retainLoadedHistory) {
            state.nextCursor = nextCursor || null;
          }
        }

        // New messages separator boundary for the currently open conversation
        try {
          state.firstNewMessageId = boundary ?? null;
        } catch {}

        if (switchingDone) state.switchingThread = false;
        state.messagesLoading = false;

        // Backfill thread avatar with first inbound message avatar (keeps thread list in sync with header)
        try {
          const inboundAvatar =
            (Array.isArray(messages) ? messages.find((m) => !m.fromMe && m.avatarUrl)?.avatarUrl : null) || null;
          if (inboundAvatar && state.selectedThreadId) {
            state.threads = (state.threads || []).map((t) =>
              t.id === state.selectedThreadId ? { ...t, avatarUrl: (t as any).avatarUrl || inboundAvatar } : t
            );
          }
        } catch {}
      })

      .addCase(loadOlderMessages.pending, (state, action) => {
        state.loadingOlder = true;
        state.olderMessagesRequestId = action.meta.requestId;
      })
      .addCase(loadOlderMessages.fulfilled, (state, action) => {
        if (state.olderMessagesRequestId !== action.meta.requestId) return;
        state.loadingOlder = false;
        state.olderMessagesRequestId = null;
        if (action.payload.threadId !== state.selectedThreadId) return;
        // Prepend messages since they are older
        const newMsgs = action.payload.messages.filter(
          nm => !state.messages.some(sm => sm.id === nm.id)
        );
        state.messages = [...newMsgs, ...state.messages];
        state.nextCursor = action.payload.nextCursor;
      })
      .addCase(loadOlderMessages.rejected, (state, action) => {
        if (state.olderMessagesRequestId !== action.meta.requestId) return;
        state.loadingOlder = false;
        state.olderMessagesRequestId = null;
      })

      .addCase(refreshMessages.rejected, (state) => {
        state.messagesLoading = false;
        state.switchingThread = false;
      });

    builder
      .addCase(sendMessage.pending, (state) => {
        state.sendingMessage = true;
      })
      .addCase(sendMessage.fulfilled, (state) => {
        state.sendingMessage = false;
      })
      .addCase(sendMessage.rejected, (state) => {
        state.sendingMessage = false;
      });

    builder.addCase(openChat.fulfilled, (state) => {
      state.isOpen = true;
    });
    builder.addCase(closeChat.fulfilled, (state) => {
      state.isOpen = false;
    });

    builder
      .addCase(initializeChatRuntime.pending, (state) => {
        state.threadsLoading = true;
      })
      .addCase(initializeChatRuntime.fulfilled, (state) => {
        state.threadsLoading = false;
        if (state.threads.length > 0) {
          state.lastThreadsRefreshAt = Date.now();
        }
      })
      .addCase(initializeChatRuntime.rejected, (state) => {
        state.threadsLoading = false;
      });
  },
});

export const {
  setIsOpen,
  setSelectedThreadId,
  setThreads,
  mergeSearchThreads,
  setMessages,
  setMessagesNextCursor,
  setUnreadCount,
  setIsConnected,
  setConnectionStatus,
  setThreadsLoading,
  setMessagesLoading,
  setSendingMessage,
  setSwitchingThread,
  setSoundEnabled,
  setDocked,
  setTyping,
  setFirstNewMessageId,
  setOpenedAt,
  setPrewarmed,
  setLastThreadsRefreshAt,
  setLastMessagesRefreshAt,
  clearThreadUnread,
  mergeThreadTags,
  openChatUI,
  closeChatUI,
  setMarkReadGuard,
} = chatSlice.actions;

export default chatSlice.reducer;

// Event wiring (attach once from store)
// Use loose types to avoid circular imports
export function attachChatListeners(store: any) {
  // Auth events in same tab
  const onAuthLogout = async () => {
    try {
      chatService.destroy();
      store.dispatch(setConnectionStatus(null));
    } catch (e) {
      console.error('Error on auth:logout destroy:', e);
    }
  };
  try {
    window.addEventListener('auth:logout', onAuthLogout as any);
  } catch {}

  // SSE connectivity
  const handleSSEConnected = () => store.dispatch(setIsConnected(true));
  const handleSSEDisconnected = () => store.dispatch(setIsConnected(false));
  const handleSSEFailed = () => {
    store.dispatch(setIsConnected(false));
    console.error('SSE connection failed');
  };

  // Threads updated
  const handleThreadsUpdated = async (payload?: any) => {
    try {
      const summary = payload?.summary;
      const incomingThreadId = payload?.thread_id || summary?.id;

      // Optimistic fallback on threads_updated for outbound bot/automation on the open thread
      // Some backends may miss emitting a dedicated "message" event for bot outbounds
      try {
        const selectedThreadId: string | null = (store.getState() as any)?.chat?.selectedThreadId || null;
        if (incomingThreadId && selectedThreadId && String(incomingThreadId) === String(selectedThreadId)) {
          const kind = String(payload?.sender_kind ?? summary?.sender_kind ?? '').toLowerCase();
          const origin = String(payload?.origin ?? summary?.origin ?? '').toLowerCase();
          const from = String(payload?.from ?? summary?.from ?? '').toLowerCase();
          const botish = (kind === 'bot' || kind === 'auto' || origin === 'n8n');
          const isOutbound = (from === 'me') || botish;

          if (botish && isOutbound) {
            const text = String(payload?.preview ?? summary?.lastMessage ?? '');
            const at = payload?.at ?? summary?.updated_time ?? new Date().toISOString();

            // Avoid duplicates if one already exists with same text and close timestamp
            const prev: Message[] = ((store.getState() as any)?.chat?.messages || []) as any;
            const FIVE_MIN = 5 * 60 * 1000;
            const norm = (s?: string) => String(s || '').trim();
            const exists = Array.isArray(prev) && prev.some((m: any) =>
              String(m?.tempId || '').startsWith('sse_bot_') &&
              m.fromMe === true &&
              norm(m?.text) === norm(text) &&
              Math.abs(Date.parse(m?.createdAt || '') - Date.parse(at)) <= FIVE_MIN
            );

            if (!exists && text) {
              const tempId = `sse_bot_${Date.now()}_${Math.random().toString(36).slice(2)}`;
              const optimistic: Message = {
                id: tempId,
                tempId,
                fromMe: true,
                text,
                imageUrl: undefined,
                createdAt: at,
                status: 'sent',
                senderKind: (payload?.sender_kind as any) ?? (summary?.sender_kind as any) ?? 'bot',
                origin: (payload?.origin as any) ?? (summary?.origin as any) ?? 'taratic_api',
                botName: (payload?.bot_name as any) ?? (summary?.bot_name as any) ?? 'bot',
              };
              chatEvents.emit('message:optimistic', { threadId: String(incomingThreadId), message: optimistic });
            }
          }
        }
      } catch (e) {
        console.error('Error emitting optimistic on threads_updated:', e);
      }

      // 1) If the currently open thread received a new message, refresh only its messages
      const selectedThreadId: string | null = (store.getState() as any)?.chat?.selectedThreadId || null;
      if (incomingThreadId && String(incomingThreadId) === String(selectedThreadId)) {
        // Guard against immediate SSE echo after our own markAsRead to avoid loops
        const guardMap = ((store.getState() as any)?.chat?.markReadGuard || {}) as Record<string, number>;
        const lastMarkAt = Number(guardMap[String(incomingThreadId)] || 0);
        const tooRecent = lastMarkAt > 0 && (Date.now() - lastMarkAt) < 1500;
        if (!tooRecent) {
          // Bypass throttling so inbound/bot messages appear instantly
          store.dispatch(setLastMessagesRefreshAt(0));
          await store.dispatch(refreshMessages());
        }
      }

      // 2) Reconcile from the service cache snapshot so its ordering and any
      // newly inserted real-time rows reach Redux.
      const cached = await chatService.getThreads(false).catch(() => []);
      if (Array.isArray(cached)) {
        // The service cache is the ordering SSOT and may include a brand-new
        // row inserted by SSE. Mapping over Redux's old rows dropped it.
        const prevThreads: any[] = ((store.getState() as any)?.chat?.threads || []) as any[];
        const prevById = new Map(prevThreads.map((t: any) => [String(t.id), t]));
        const mergedAll = cached.map((t: any) => {
          const prev = prevById.get(String(t.id));
          const prevTags = Array.isArray(prev?.tags) ? prev.tags : [];
          return prevTags.length
            ? { ...t, tags: prevTags }
            : { ...t, tags: Array.isArray((t as any).tags) ? (t as any).tags : [] };
        });
        store.dispatch(setThreads(mergedAll));
        if (!incomingThreadId) {
          await store.dispatch(loadThreadTagsForThreads(undefined) as any);
        }
        // Prewarm top threads after a full list refresh
        await store.dispatch(prewarmTopMessages() as any);
      }
    } catch (e) {
      console.error('Error handling threads_updated:', e);
    }
  };

  // Message handlers
  const handleMessageReceived = async (data: any) => {
    const { thread_id } = data || {};
    const selectedThreadId: string | null = (store.getState() as any)?.chat?.selectedThreadId || null;

    const senderKind = String(data?.sender_kind || '').toLowerCase();
    const origin = String(data?.origin || '').toLowerCase();
    const isInstagramOperatorMessage =
      data?.from === 'me' && senderKind === 'operator' && origin === 'instagram';

    // Locally initiated bot/web sends already have an optimistic canonical row.
    // A human message typed in the Instagram app also has from='me', but it was
    // not initiated by this browser and must refresh the open chat.
    if (data.from === 'me' && !isInstagramOperatorMessage) {
      console.log('[DEBUG] Skipping refresh for outbound message to avoid Graph API lag race');
      // Still emit for threads list update (lastMessage, unread=0 if applicable)
      if (thread_id) {
        const cached = await chatService.getThreads(false).catch(() => []);
        if (Array.isArray(cached)) {
          // Preserve existing per-thread tags to keep row chips always visible
          const prevThreads: any[] = ((store.getState() as any)?.chat?.threads || []) as any[];
          const prevById = new Map(prevThreads.map((t: any) => [String(t.id), t]));
          const merged = cached.map((t: any) => {
            const prev = prevById.get(String(t.id));
            const prevTags = Array.isArray(prev?.tags) ? prev.tags : [];
            return prevTags.length
              ? { ...t, tags: prevTags }
              : { ...t, tags: Array.isArray((t as any).tags) ? (t as any).tags : [] };
          });
          store.dispatch(setThreads(merged));
        }
      }
      return;
    }

    // Redux-level optimistic fallback for outbound bot/automation messages on the open thread
    try {
      const from = String(data?.from || '').toLowerCase();
      const kind = String(data?.sender_kind || '').toLowerCase();
      const origin = String(data?.origin || '').toLowerCase();
      const isBotish = (kind === 'bot' || kind === 'auto' || origin === 'n8n');
      const isOutbound = (from === 'me') || isBotish;

      if (thread_id && selectedThreadId && String(thread_id) === String(selectedThreadId) && isOutbound && isBotish) {
        const text = String(data?.preview || '');
        const at = data?.at || new Date().toISOString();

        const prev: Message[] = ((store.getState() as any)?.chat?.messages || []) as any;
        const FIVE_MIN = 5 * 60 * 1000;
        const norm = (s?: string) => String(s || '').trim();

        const alreadyOptimistic = Array.isArray(prev) && prev.some((m: any) =>
          String(m?.tempId || '').startsWith('sse_bot_') &&
          m.fromMe === true &&
          norm(m?.text) === norm(text) &&
          Math.abs(Date.parse(m?.createdAt || '') - Date.parse(at)) <= FIVE_MIN
        );

        if (!alreadyOptimistic) {
          const tempId = `sse_bot_${Date.now()}_${Math.random().toString(36).slice(2)}`;
          const optimistic: Message = {
            id: tempId,
            tempId,
            fromMe: true,
            text,
            imageUrl: undefined,
            createdAt: at,
            status: 'sent',
            senderKind: (data?.sender_kind as any) || 'bot',
            origin: (data?.origin as any) || 'taratic_api',
            botName: data?.bot_name || 'bot',
          };
          chatEvents.emit('message:optimistic', { threadId: thread_id, message: optimistic });
        }
      }
    } catch (e) {
      console.error('Error emitting optimistic in redux listener:', e);
    }

    // Fetch messages immediately for the open thread (bypass throttle/flags) — only for inbound
    if (thread_id && String(thread_id) === String(selectedThreadId)) {
      try {
        // Ensure any throttles are bypassed so bot messages appear instantly
        store.dispatch(setLastMessagesRefreshAt(0));
        await store.dispatch(refreshMessages({ forceNetwork: true }) as any);
      } catch (e) {
        console.error('Error refreshing messages on SSE message:', e);
      }
    } else if (thread_id) {
      // For other threads, sync threads from cache to reflect per-row badges instantly
      const cached = await chatService.getThreads(false).catch(() => []);
      if (Array.isArray(cached)) {
        // Preserve existing per-thread tags to keep row chips always visible
        const prevThreads: any[] = ((store.getState() as any)?.chat?.threads || []) as any[];
        const prevById = new Map(prevThreads.map((t: any) => [String(t.id), t]));
        const merged = cached.map((t: any) => {
          const prev = prevById.get(String(t.id));
          const prevTags = Array.isArray(prev?.tags) ? prev.tags : [];
          return prevTags.length
            ? { ...t, tags: prevTags }
            : { ...t, tags: Array.isArray((t as any).tags) ? (t as any).tags : [] };
        });
        store.dispatch(setThreads(merged));
      }
    }
  };

  const handleMessageOptimistic = ({ threadId, message }: any) => {
    const selectedThreadId: string | null = (store.getState() as any)?.chat?.selectedThreadId || null;
    if (String(threadId) === String(selectedThreadId)) {
      const prev: Message[] = (store.getState() as any)?.chat?.messages || [];
      store.dispatch(setMessages([...prev, message]));
    }
    // Optimistically patch thread row (lastMessage/updated_time) without forcing a full list refresh.
    // Do NOT reorder here; SSOT order is maintained by ChatService via cache + SSE.
    try {
      const prevThreads: any[] = ((store.getState() as any)?.chat?.threads || []) as any[];
      const patched = prevThreads.map((t: any) =>
        String(t.id) === String(threadId)
          ? {
              ...t,
              lastMessage:
                message?.text ??
                (message?.imageUrl ? 'Imagen' : (t as any).lastMessage),
              updated_time: message?.createdAt || new Date().toISOString(),
            }
          : t
      );
      store.dispatch(setThreads(patched));
    } catch {}
  };

  const handleMessageSent = ({ threadId, tempId, message }: any) => {
    const selectedThreadId: string | null = (store.getState() as any)?.chat?.selectedThreadId || null;
    if (String(threadId) === String(selectedThreadId)) {
      const prev: Message[] = (store.getState() as any)?.chat?.messages || [];
      const next = prev.map((m) => ((m as any).tempId === tempId ? message : m)) as Message[];
      store.dispatch(setMessages(next));
    }
    // Also patch thread row on confirm (in case SSE is delayed)
    try {
      const prevThreads: any[] = ((store.getState() as any)?.chat?.threads || []) as any[];
      const patched = prevThreads.map((t: any) =>
        String(t.id) === String(threadId)
          ? {
              ...t,
              lastMessage:
                message?.text ??
                (message?.imageUrl ? 'Imagen' : (t as any).lastMessage),
              updated_time: message?.createdAt || new Date().toISOString(),
            }
          : t
      );
      store.dispatch(setThreads(patched));
    } catch {}
  };

  const handleMessageError = ({ threadId, tempId }: any) => {
    const selectedThreadId: string | null = (store.getState() as any)?.chat?.selectedThreadId || null;
    if (String(threadId) === String(selectedThreadId)) {
      const prev: Message[] = (store.getState() as any)?.chat?.messages || [];
      const next = prev.map((m) => (((m as any).tempId === tempId ? { ...m, status: 'error' as const } : m))) as Message[];
      store.dispatch(setMessages(next));
    }
  };

  const handleUnreadCount = (count: number) => {
    store.dispatch(setUnreadCount(count));
    // Intentionally do NOT re-sync the full threads list here to avoid reordering
    // when a thread is marked as read on selection. The selected row is already
    // cleared locally by selectThread() -> clearThreadUnread().
  };

  const handleThreadTyping = ({ threadId, typing }: any) => {
    store.dispatch(setTyping({ threadId, typing: !!typing }));
  };

  const handleNotificationClicked = (threadId: string) => {
    store.dispatch(setIsOpen(true));
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    store.dispatch(selectThread(threadId));
  };

  // Pinned updated => re-sync list from cache
  const handlePinnedUpdated = async () => {
    const cached = await chatService.getThreads(false).catch(() => []);
    if (Array.isArray(cached)) {
      const prevThreads: any[] = ((store.getState() as any)?.chat?.threads || []) as any[];
      const prevById = new Map(prevThreads.map((t: any) => [String(t.id), t]));
      const merged = cached.map((t: any) => {
        const prev = prevById.get(String(t.id));
        const prevTags = Array.isArray(prev?.tags) ? prev.tags : [];
        return prevTags.length
          ? { ...t, tags: prevTags }
          : { ...t, tags: Array.isArray((t as any).tags) ? (t as any).tags : [] };
      });
      store.dispatch(setThreads(merged));
    }
  };

  // RTK Query: Invalidate tags caches on SSE events
  const invalidateAllTags = () => {
    try {
      store.dispatch(tagsApi.util.invalidateTags(['Tags', 'Folders']));
    } catch {}
  };
  const invalidateThreadTags = (threadId: string) => {
    try {
      store.dispatch(tagsApi.util.invalidateTags([{ type: 'ThreadTags', id: String(threadId) }, 'Tags']));
    } catch {}
  };
  const handleTagsServer = (_payload?: any) => {
    invalidateAllTags();
  };
  const handleThreadTagsServer = (payload?: any) => {
    const threadId = payload?.thread_id ?? payload?.threadId ?? payload?.id ?? null;
    if (threadId) {
      invalidateThreadTags(String(threadId));
      const tags = Array.isArray(payload?.tags) ? payload.tags : [];
      // Merge into Redux chat.threads so rows show immediately
      store.dispatch(mergeThreadTags({ [String(threadId)]: tags }));
    } else {
      invalidateAllTags();
    }
  };

  // Subscribe
  chatService.on('sse:connected', handleSSEConnected);
  chatService.on('tags:server', handleTagsServer);
  chatService.on('thread-tags:server', handleThreadTagsServer);
  chatService.on('sse:disconnected', handleSSEDisconnected);
  chatService.on('sse:failed', handleSSEFailed);
  chatService.on('threads:updated', handleThreadsUpdated);
  chatService.on('message:received', handleMessageReceived);
  chatService.on('message:optimistic', handleMessageOptimistic);
  chatService.on('message:sent', handleMessageSent);
  chatService.on('message:error', handleMessageError);
  chatService.on('unread:count', handleUnreadCount);
  chatService.on('thread:typing', handleThreadTyping);
  chatService.on('notification:clicked', handleNotificationClicked);
  chatService.on('pinned:updated', handlePinnedUpdated);

  // The authenticated layout starts snapshots after demo status is resolved.
}
